import { useCallback, useMemo } from "react"
import { SupportedTokenName } from "@mezo/sdk"
import {
  Block,
  HeadingXSmall,
  ListItem,
  useStyletron,
} from "@mezo-org/mezo-clay"

import superstackDepositIcon from "#/assets/superstack-circle.svg"
import tbtcTokenLogo from "#/assets/tokens/tbtc-token.svg"
import CurrencySymbol from "#/shared/components/CurrencySymbol"

type TokenLabelProps = {
  token: SupportedTokenName
  tokenIcon: string
  amount: string | number
  showDepositMigrationButton: boolean
  showSuperstackIcon?: boolean
  isMigratedToTbtc?: boolean
  onClick: () => void
}

export default function TokenLabel({
  token,
  tokenIcon,
  amount,
  showDepositMigrationButton,
  showSuperstackIcon = false,
  isMigratedToTbtc,
  onClick,
}: TokenLabelProps) {
  const [css, theme] = useStyletron()

  const displayTokenIcon = useMemo(
    () => (isMigratedToTbtc ? tbtcTokenLogo : tokenIcon),
    [isMigratedToTbtc, tokenIcon],
  )

  const tokenImage = useCallback(
    () => (
      <Block className={css({ display: "flex", alignItems: "center" })}>
        {showSuperstackIcon && (
          <Block
            as="img"
            src={superstackDepositIcon}
            className={css({
              background: theme.colors.white,
              borderRadius: "100%",
              border: "1px solid white",
              height: "26px",
              width: "26px",
              objectFit: "contain",
            })}
          />
        )}
        <Block
          as="img"
          src={displayTokenIcon}
          className={css({
            background: theme.colors.white,
            borderRadius: "100%",
            border: showSuperstackIcon ? "1px solid white" : "none",
            height: showSuperstackIcon ? "26px" : theme.sizing.scale800,
            width: showSuperstackIcon ? "26px" : theme.sizing.scale800,
            objectFit: "contain",
            marginLeft: showSuperstackIcon ? `-${theme.sizing.scale300}` : 0,
          })}
        />
      </Block>
    ),
    [displayTokenIcon, theme, css, showSuperstackIcon],
  )

  return (
    <ListItem
      artwork={tokenImage}
      overrides={{
        ArtworkContainer: {
          style: () => ({
            marginLeft: "auto",
            justifyContent: "flex-start",
            paddingLeft: "20px",
            width: showSuperstackIcon ? "80px" : theme.sizing.scale1600,
          }),
        },
        Content: {
          style: () => ({
            borderBottom: 0,
            width: "max-content",
            flexGrow: 0,
            paddingRight: 0,
          }),
        },
      }}
    >
      <HeadingXSmall
        className={css({
          "@media screen and (max-width: 1330px)": {
            fontSize: "16px",
          },
          "@media screen and (max-width: 1240px)": {
            fontSize: "11px",
          },
          "@media screen and (max-width: 1136px)": {
            fontSize: "20px",
          },
        })}
      >
        {amount}{" "}
        {showDepositMigrationButton ? (
          <Block
            as="button"
            onClick={onClick}
            className={css({
              display: "inline-block",
              borderBottomColor: theme.colors.black,
              borderBottomWidth: "2px",
            })}
          >
            <CurrencySymbol token={token} /> → <CurrencySymbol token="tBTC" />
          </Block>
        ) : (
          <CurrencySymbol token={token} />
        )}
      </HeadingXSmall>
    </ListItem>
  )
}
